import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { useTranslation } from 'react-i18next';

interface InstallmentDataItem {
  installments: number | null;
  cantidad: number;
  porcentaje: number;
}

interface InstallmentsChartProps {
  data: InstallmentDataItem[];
  title: string;
  subtitle?: string;
}

const InstallmentsChart: React.FC<InstallmentsChartProps> = ({ data, title, subtitle }) => {
  const { t } = useTranslation();

  const formatNumber = (value: number): string => new Intl.NumberFormat('es-ES').format(value);
  const formatPercentage = (value: number): string => `${value.toFixed(1)}%`;

  const getBarColor = (index: number) => {
    const colors = [
      '#10B981',
      '#3B82F6',
      '#8B5CF6',
      '#EC4899',
      '#F59E0B',
      '#EF4444',
      '#06B6D4',
      '#84CC16',
      '#F97316',
      '#6366F1',
      '#9333EA',
      '#FB7185'
    ];

    return colors[index % colors.length];
  };

  const getInstallmentLabel = (count: number | null) => {
    if (count === null) {
      return t('statistics.installments.no_installments');
    }
    const key = count === 1
      ? 'statistics.installments.installment_label'
      : 'statistics.installments.installment_label_plural';
    return t(key, { count });
  };

  const chartData = useMemo(() => {
    if (!data) return [];
    return data
      .filter(item => item.cantidad > 0)
      .map(item => ({
        ...item,
        label: getInstallmentLabel(item.installments)
      }));
  }, [data, t]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const tooltipData = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{tooltipData.label}</p>
          <p className="text-blue-600">
            {`${t('statistics.common_labels.quantity')}: ${formatNumber(tooltipData.cantidad)} ${t('statistics.common_labels.publications')}`}
          </p>
          <p className="text-gray-600">
            {`${t('statistics.common_labels.percentage')}: ${formatPercentage(tooltipData.porcentaje)}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, payload, value } = props;
    if (height < 25) return null;

    const cantidad = payload?.cantidad ?? value ?? 0;
    const _porcentaje = payload?.porcentaje;

    const labelX = x + width / 2;
    const labelY = y + height / 2;

    return (
      <text
        x={labelX}
        y={labelY}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="11"
        fontWeight="bold"
        style={{
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.5))'
        }}
      >
        {formatNumber(cantidad)}
      </text>
    );
  };

  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
        {subtitle && <p className="text-gray-600">{subtitle}</p>}
      </div>

      <div className="h-80 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 15, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="label"
              interval={0}
              tick={{ fontSize: 11 }}
              height={50}
            />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="cantidad" radius={[4, 4, 0, 0]}>
              <LabelList content={renderCustomLabel} />
              {chartData.map((entry, index) => (
                <Cell key={`cell-${entry.label}-${index}`} fill={getBarColor(index)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto max-w-4xl mx-auto">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('statistics.table_headers.installments')}
              </th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('statistics.table_headers.quantity')}
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('statistics.table_headers.percentage')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {chartData.map((item, index) => (
              <tr key={item.label} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded mr-2"
                      style={{ backgroundColor: getBarColor(index) }}
                    />
                    <div className="text-sm font-medium text-gray-900">
                      {item.label}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  <div className="text-sm text-gray-900 font-medium">
                    {formatNumber(item.cantidad)}
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  <div className="text-sm text-gray-900 font-medium">
                    {formatPercentage(item.porcentaje)}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1 max-w-[100px] ml-auto">
                    <div
                      className="h-1.5 rounded-full"
                      style={{
                        width: `${Math.min(item.porcentaje, 100)}%`,
                        backgroundColor: getBarColor(index)
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InstallmentsChart;
